import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/lib/utils";

type ScrollEdges = "none" | "top" | "bottom" | "both";

const FADE_CLASSES = {
  sidebar: { from: "from-sidebar", icon: "text-sidebar-foreground/50 hover:text-sidebar-foreground/80" },
  background: { from: "from-background", icon: "text-foreground/50 hover:text-foreground/80" },
} as const;

interface ScrollIndicatorAreaProps extends React.ComponentProps<"div"> {
  /** Surface the area sits on; the edge fades blend into it. */
  surface?: keyof typeof FADE_CLASSES;
  /** Classes for the positioned wrapper around the scroll container. */
  wrapperClassName?: string;
}

/** Slack for fractional scroll offsets under browser zoom or display scaling. */
const EDGE_TOLERANCE_PX = 2;
/** Pixels per wheel step in line mode (Firefox on Windows reports lines). */
const WHEEL_LINE_PX = 16;

/** The wheel delta in pixels, whatever unit the browser reports. */
function wheelDeltaPx(event: WheelEvent, pageHeight: number): number {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * WHEEL_LINE_PX;
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * pageHeight;
  return event.deltaY;
}

/**
 * Vertical scroll container without a visible scrollbar. A fade with a
 * bouncing chevron at the top and/or bottom edge shows that more content is
 * hidden in that direction; clicking the chevron scrolls by 100px.
 *
 * The fades never take pointer events: clicks and the mouse wheel reach the
 * entries under them. Only the chevron is a target, and the wheel over it
 * scrolls the area too. Each fade is a sibling of the scroll container, so
 * without this the wheel over a fade scrolled nothing and the half-hidden
 * entry under it could not be clicked.
 */
const ScrollIndicatorArea = React.forwardRef<HTMLDivElement, ScrollIndicatorAreaProps>(
  ({ className, wrapperClassName, surface = "sidebar", ...props }, ref) => {
    const scrollRef = React.useRef<HTMLDivElement | null>(null);
    const [edges, setEdges] = React.useState<ScrollEdges>("none");

    // Merge forwarded ref with internal ref
    const mergedRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        scrollRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    React.useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;

      const update = () => {
        const { scrollTop, scrollHeight, clientHeight } = el;
        const atTop = scrollTop <= EDGE_TOLERANCE_PX;
        const atBottom = scrollTop + clientHeight >= scrollHeight - EDGE_TOLERANCE_PX;

        if (scrollHeight - clientHeight <= EDGE_TOLERANCE_PX) setEdges("none");
        else if (atTop) setEdges("bottom");
        else if (atBottom) setEdges("top");
        else setEdges("both");
      };

      el.addEventListener("scroll", update, { passive: true });
      const ro = new ResizeObserver(update);
      ro.observe(el);
      // Content changes (e.g. a filtered list) alter scrollHeight without resizing the container.
      const mo = new MutationObserver(update);
      mo.observe(el, { childList: true, subtree: true });
      update();

      return () => {
        el.removeEventListener("scroll", update);
        ro.disconnect();
        mo.disconnect();
      };
    }, []);

    const showTop = edges === "top" || edges === "both";
    const showBottom = edges === "bottom" || edges === "both";
    const fade = FADE_CLASSES[surface];

    const scrollBy = React.useCallback((delta: number) => {
      scrollRef.current?.scrollBy({ top: delta, behavior: "smooth" });
    }, []);

    // The wheel over a chevron scrolls the area, not the page behind it. A
    // native listener, because React registers wheel handlers as passive.
    const topChevronRef = React.useRef<HTMLButtonElement | null>(null);
    const bottomChevronRef = React.useRef<HTMLButtonElement | null>(null);
    React.useEffect(() => {
      const chevrons = [topChevronRef.current, bottomChevronRef.current];
      const onWheel = (event: WheelEvent) => {
        const el = scrollRef.current;
        // Ctrl-wheel and trackpad pinch zoom the page; leave them to the browser.
        if (!el || event.ctrlKey) return;
        event.preventDefault();
        el.scrollBy({ top: wheelDeltaPx(event, el.clientHeight) });
      };
      chevrons.forEach((chevron) => chevron?.addEventListener("wheel", onWheel, { passive: false }));
      return () => chevrons.forEach((chevron) => chevron?.removeEventListener("wheel", onWheel));
    }, []);

    return (
      <div className={cn("relative flex min-h-0 flex-1 flex-col", wrapperClassName)}>
        {/* Top scroll indicator */}
        <div
          data-testid="scroll-indicator-top"
          data-visible={showTop}
          className={cn(
            "pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-center bg-gradient-to-b from-30% to-transparent transition-all duration-200",
            fade.from,
            showTop ? "h-8 opacity-100" : "h-0 opacity-0"
          )}
        >
          <button
            ref={topChevronRef}
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => scrollBy(-100)}
            className={cn(
              "flex h-6 w-10 cursor-pointer items-center justify-center transition-colors",
              showTop ? "pointer-events-auto" : "pointer-events-none",
              fade.icon
            )}
          >
            <ChevronUp className="h-4 w-4 animate-bounce" />
          </button>
        </div>
        <div
          ref={mergedRef}
          className={cn(
            // scroll-py-8: keyboard focus and scrollIntoView stop clear of the h-8 fades.
            "flex min-h-0 flex-1 flex-col overflow-auto scroll-py-8",
            "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
            className
          )}
          {...props}
        />
        {/* Bottom scroll indicator */}
        <div
          data-testid="scroll-indicator-bottom"
          data-visible={showBottom}
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center justify-center bg-gradient-to-t from-30% to-transparent transition-all duration-200",
            fade.from,
            showBottom ? "h-8 opacity-100" : "h-0 opacity-0"
          )}
        >
          <button
            ref={bottomChevronRef}
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => scrollBy(100)}
            className={cn(
              "flex h-6 w-10 cursor-pointer items-center justify-center transition-colors",
              showBottom ? "pointer-events-auto" : "pointer-events-none",
              fade.icon
            )}
          >
            <ChevronDown className="h-4 w-4 animate-bounce" />
          </button>
        </div>
      </div>
    );
  }
);
ScrollIndicatorArea.displayName = "ScrollIndicatorArea";

export { ScrollIndicatorArea };
